import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{2,32}$/;

function getAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return {
    'Content-Type': 'application/json',
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
  };
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function displayUser(user) {
  if (!user) return '—';
  if (user.name && user.username) return `${user.name} (@${user.username})`;
  return user.name || user.username || user.email || user.phone || `User #${user.userId}`;
}

function ReferralCodes() {
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [hasCode, setHasCode] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ limit: PAGE_SIZE, offset: 0, hasMore: false, total: 0 });
  const [defaultJoinBonusFe, setDefaultJoinBonusFe] = useState(100);

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editReferralCode, setEditReferralCode] = useState('');
  const [editJoinBonusFe, setEditJoinBonusFe] = useState('');
  const [useDefaultBonus, setUseDefaultBonus] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (query) params.set('q', query);
    if (hasCode === 'true' || hasCode === 'false') params.set('hasCode', hasCode);
    return params.toString();
  }, [offset, query, hasCode]);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const requestParams = new URLSearchParams(queryString);
      requestParams.set('_ts', String(Date.now()));

      const response = await fetch(`${BASE_URL}/admin/referrals/codes?${requestParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(result?.message || 'Failed to fetch referral codes.');
        setUsers([]);
        return;
      }

      const data = result?.data || {};
      setUsers(Array.isArray(data.users) ? data.users : []);
      setPagination(data.pagination || { limit: PAGE_SIZE, offset, hasMore: false, total: 0 });
      if (data.defaultJoinBonusFe != null) {
        setDefaultJoinBonusFe(data.defaultJoinBonusFe);
      }
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch referral codes.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [queryString, offset]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const runSearch = () => {
    setOffset(0);
    setQuery(searchInput.trim());
  };

  const handleHasCodeChange = (value) => {
    setOffset(0);
    setHasCode(value);
  };

  const openEdit = async (user) => {
    setEditError('');
    setSuccess('');
    setEditUser(user);
    setEditReferralCode(user.referralCode || '');
    setUseDefaultBonus(user.joinBonusFe == null);
    setEditJoinBonusFe(
      user.joinBonusFe != null ? String(user.joinBonusFe) : String(user.effectiveJoinBonusFe ?? defaultJoinBonusFe)
    );
    setEditOpen(true);

    try {
      const response = await fetch(`${BASE_URL}/admin/referrals/users/${user.userId}`, {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) return;

      const fresh = result?.data;
      if (!fresh) return;

      setEditUser((prev) => ({ ...prev, ...fresh }));
      setEditReferralCode(fresh.referralCode || '');
      setUseDefaultBonus(fresh.joinBonusFe == null);
      setEditJoinBonusFe(
        fresh.joinBonusFe != null
          ? String(fresh.joinBonusFe)
          : String(fresh.effectiveJoinBonusFe ?? fresh.defaultJoinBonusFe ?? defaultJoinBonusFe)
      );
      if (fresh.defaultJoinBonusFe != null) {
        setDefaultJoinBonusFe(fresh.defaultJoinBonusFe);
      }
    } catch {
      // Keep list-row data if refresh fails
    }
  };

  const closeEdit = () => {
    if (editLoading) return;
    setEditOpen(false);
    setEditUser(null);
    setEditError('');
  };

  const handleSave = async () => {
    if (!editUser) return;
    setEditError('');

    const originalCode = editUser.referralCode || '';
    const nextCode = editReferralCode.trim();
    const codeChanged = nextCode !== originalCode;
    const originalBonus = editUser.joinBonusFe;
    const nextBonus = useDefaultBonus ? null : Number(editJoinBonusFe);
    const bonusChanged = useDefaultBonus
      ? originalBonus != null
      : originalBonus == null || Number(originalBonus) !== nextBonus;

    if (!codeChanged && !bonusChanged) {
      setEditError('Change the referral code or join bonus before saving.');
      return;
    }

    if (codeChanged) {
      if (!nextCode) {
        setEditError('Referral code is required when updating it.');
        return;
      }
      if (!REFERRAL_CODE_PATTERN.test(nextCode)) {
        setEditError('Referral code must be 2–32 characters: letters, numbers, _ or - only.');
        return;
      }
    }

    if (!useDefaultBonus) {
      if (editJoinBonusFe === '' || Number.isNaN(nextBonus) || !Number.isInteger(nextBonus)) {
        setEditError('Join Fan Energy must be a whole number, or use the default.');
        return;
      }
      if (nextBonus < 0 || nextBonus > 1000000) {
        setEditError('Join Fan Energy must be between 0 and 1,000,000.');
        return;
      }
    }

    const body = {};
    if (codeChanged) body.referralCode = nextCode;
    if (bonusChanged) body.joinBonusFe = nextBonus;

    setEditLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/admin/referrals/users/${editUser.userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setEditError(result?.message || 'Failed to update referral user.');
        return;
      }

      const updated = result?.data || {};
      setUsers((prev) =>
        prev.map((row) =>
          Number(row.userId) === Number(editUser.userId)
            ? {
                ...row,
                ...updated,
                referredUsersCount: row.referredUsersCount,
                createdAt: row.createdAt,
              }
            : row
        )
      );
      setSuccess(result?.message || 'Referral user updated successfully.');
      setEditOpen(false);
      setEditUser(null);
    } catch (saveError) {
      setEditError(saveError.message || 'Failed to update referral user.');
    } finally {
      setEditLoading(false);
    }
  };

  const hasMore = Boolean(pagination?.hasMore);
  const total = pagination?.total ?? 0;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="d-flex align-items-center">
                    <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                      <ArrowBackIcon />
                    </IconButton>
                    <h3 style={{ marginLeft: '20px', marginBottom: 0 }}>Referral Codes</h3>
                  </div>
                  <Button component={Link} to="/admin/referrals-dashboard" variant="outlined" size="small">
                    Referral analytics
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Default join bonus: {defaultJoinBonusFe} FE (unless overridden). Subscribers who use a
                    code receive this Fan Energy once.
                  </Alert>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Search"
                        placeholder="Username, name, email, phone, or code"
                        fullWidth
                        size="small"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') runSearch();
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Has code"
                        select
                        fullWidth
                        size="small"
                        value={hasCode}
                        onChange={(event) => handleHasCodeChange(event.target.value)}
                      >
                        <MenuItem value="">All users</MenuItem>
                        <MenuItem value="true">With code</MenuItem>
                        <MenuItem value="false">Without code</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button variant="contained" onClick={runSearch} disabled={loading} fullWidth sx={{ height: '100%' }}>
                        Search
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSearchInput('');
                          setQuery('');
                          setHasCode('');
                          setOffset(0);
                        }}
                        disabled={loading}
                        fullWidth
                        sx={{ height: '100%' }}
                      >
                        Reset
                      </Button>
                    </Grid>
                  </Grid>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                      {success}
                    </Alert>
                  )}

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Referral code</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Join FE bonus</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Referred users
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Box sx={{ py: 4 }}>
                                <CircularProgress size={28} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}

                        {!loading && users.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                No users found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {!loading &&
                          users.map((user) => {
                            const isCustomBonus = user.joinBonusFe != null;
                            return (
                              <TableRow key={user.userId} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {displayUser(user)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {user.email || user.phone || `ID ${user.userId}`}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {user.referralCode ? (
                                    <Typography variant="body2" fontFamily="monospace">
                                      {user.referralCode}
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        —
                                      </Typography>
                                      <Button size="small" onClick={() => openEdit(user)}>
                                        Assign
                                      </Button>
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">{user.effectiveJoinBonusFe ?? defaultJoinBonusFe}</Typography>
                                    {isCustomBonus && <Chip label="Custom" size="small" color="primary" variant="outlined" />}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">{user.referredUsersCount ?? 0}</TableCell>
                                <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" onClick={() => openEdit(user)} aria-label="Edit referral code">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, alignItems: 'center' }}>
                    <Button variant="outlined" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0 || loading}>
                      Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Showing {users.length ? offset + 1 : 0}–{offset + users.length}
                      {total ? ` of ${total}` : ''} | Offset: {offset} | Limit: {PAGE_SIZE}
                    </Typography>
                    <Button variant="outlined" onClick={() => setOffset((prev) => prev + PAGE_SIZE)} disabled={!hasMore || loading}>
                      Next
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit referral — {displayUser(editUser)}</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          <TextField
            label="Referral code"
            fullWidth
            margin="normal"
            value={editReferralCode}
            onChange={(event) => setEditReferralCode(event.target.value)}
            helperText="2–32 characters: A–Z, a–z, 0–9, _ or -"
            inputProps={{ maxLength: 32 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={useDefaultBonus}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setUseDefaultBonus(checked);
                  if (checked) {
                    setEditJoinBonusFe(String(defaultJoinBonusFe));
                  }
                }}
              />
            }
            label={`Use default (${defaultJoinBonusFe} FE)`}
          />
          <TextField
            label="Join Fan Energy"
            type="number"
            fullWidth
            margin="normal"
            value={editJoinBonusFe}
            onChange={(event) => setEditJoinBonusFe(event.target.value)}
            disabled={useDefaultBonus}
            helperText="Subscribers who use this code get this Fan Energy once. Default is usually 100."
            inputProps={{ min: 0, max: 1000000, step: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={editLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={editLoading}>
            {editLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Component>
  );
}

export default ReferralCodes;
